/**
 * sl-srag.3.2: pure grouping + bounded-rendering model for QuickSearch docs
 * results.
 *
 * Keeps the result-shaping logic ("group by doc, cap visible sources, expose a
 * load-more affordance") separate from the framework-scoped session that turns
 * the rows into `QuickSearchItem`s with icons / i18n / click handlers (SOLID).
 * This module is pure and serialisable, so the flood-protection contract is
 * unit-testable without a DOM or the DI container.
 *
 * Flood protection (a doc / attachment with many hits must never emit an
 * unbounded list): every place that hides rows reports the hidden count so the
 * renderer can show an explicit "+N more" load-more row — no silent slicing.
 */
import type { SearchSourceType } from '@affine/core/modules/docs-search';

import { getDocsSearchLocationIdentity } from '../../docs-search/services/source-navigation';
import {
  type AggregatedPdfDoc,
  aggregatePdfHitsByDoc,
  type DocsSearchResult,
  filterBlockResultsShadowedByPdfHits,
  type PdfPageSearchHit,
} from './docs-pdf-search';

/** Block source rows shown inside one expanded doc before a load-more row. */
export const BLOCK_SOURCES_PER_DOC_LIMIT = 3;
/** PDF page rows shown inside one expanded attachment before a load-more row. */
export const PDF_PAGES_PER_ATTACHMENT_LIMIT = 5;

/** Stable load-more list keys, namespaced so block and PDF lists never clash. */
export const blockSourcesListKey = (docId: string) => `block-sources:${docId}`;
export const pdfPagesListKey = (docId: string, attachmentBlockId: string) =>
  `pdf-pages:${docId}:${attachmentBlockId}`;

/**
 * Generic visible / hidden split with an explicit load-more contract: when a
 * list exceeds `limit` and is not force-expanded, show the first `limit` items
 * and report how many remain. `hidden === 0` means the whole list is shown.
 */
export function splitForLoadMore<T>(
  items: readonly T[],
  limit: number,
  showAll: boolean
): { visible: T[]; hidden: number } {
  if (showAll || items.length <= limit) {
    return { visible: [...items], hidden: 0 };
  }
  return { visible: items.slice(0, limit), hidden: items.length - limit };
}

export interface BlockDocGroup {
  docId: string;
  /** Highest-scoring hit for this doc; drives the header / standalone row. */
  best: DocsSearchResult;
  /** All hits for this doc, in arrival (score-desc) order, `best` included. */
  sources: DocsSearchResult[];
}

export interface SearchDocBucket {
  docId: string;
  score: number;
  sourceTypes: SearchSourceType[];
  pdf?: AggregatedPdfDoc;
  block?: BlockDocGroup;
}

/**
 * Header badges describe the rows a user can reveal at the next level, not
 * raw index occurrences hidden inside those rows.
 */
export function countSearchDocBucketChildren(bucket: SearchDocBucket): number {
  return (
    (bucket.pdf?.attachments.length ?? 0) + (bucket.block?.sources.length ?? 0)
  );
}

export function countPdfAttachmentChildren(
  attachment: AggregatedPdfDoc['attachments'][number]
): number {
  return attachment.pages.length;
}

/**
 * One top-level search result row per document. PDF page search, local block
 * search and remote database/doc search can all return hits for the same doc;
 * the UI must merge them here instead of rendering one top-level row per
 * backend source. This keeps local-first/remote merge semantics out of the
 * view layer and makes expand/collapse insert children without reordering the
 * surrounding docs.
 */
export function buildSearchDocBuckets(
  blockResults: readonly DocsSearchResult[],
  pdfHits: readonly PdfPageSearchHit[]
): SearchDocBucket[] {
  const buckets = new Map<string, SearchDocBucket>();

  for (const pdf of aggregatePdfHitsByDoc([...pdfHits])) {
    buckets.set(pdf.docId, {
      docId: pdf.docId,
      score: pdf.bestHit.score,
      sourceTypes: ['pdf'],
      pdf,
    });
  }

  const blockGroups = groupBlockResultsByDoc(
    dedupeBlockResultsByLocation(
      filterBlockResultsShadowedByPdfHits([...blockResults], [...pdfHits])
    )
  );
  for (const block of blockGroups) {
    const existing = buckets.get(block.docId);
    if (existing) {
      existing.block = block;
      existing.score = Math.max(existing.score, block.best.score);
      existing.sourceTypes = uniqueSourceTypes([
        ...existing.sourceTypes,
        ...block.sources.map(source => source.sourceType),
      ]);
    } else {
      buckets.set(block.docId, {
        docId: block.docId,
        score: block.best.score,
        sourceTypes: uniqueSourceTypes(
          block.sources.map(source => source.sourceType)
        ),
        block,
      });
    }
  }

  return [...buckets.values()].sort((a, b) => b.score - a.score);
}

/**
 * Group flat block results by doc, preserving the incoming (score-descending)
 * order both across docs and within a doc. The first hit seen for a doc is its
 * `best` because the aggregate emits hits in score order.
 */
export function groupBlockResultsByDoc(
  results: readonly DocsSearchResult[]
): BlockDocGroup[] {
  const groups = new Map<string, BlockDocGroup>();
  for (const result of results) {
    const existing = groups.get(result.docId);
    if (existing) {
      existing.sources.push(result);
    } else {
      groups.set(result.docId, {
        docId: result.docId,
        best: result,
        sources: [result],
      });
    }
  }
  return [...groups.values()];
}

/**
 * Collapse multiple index rows that point at the same navigable search target.
 *
 * Search can return the same physical block/cell/edgeless element through
 * several sources (remote aggregate + local aggregate, or several snippets from
 * one indexed block). The UI can only navigate to the physical location, so
 * keeping each snippet as a separate row creates duplicate rows and duplicate
 * QuickSearch keys. Preserve the first row's order, keep the strongest score,
 * and use the strongest row's snippet/title when it wins.
 */
export function dedupeBlockResultsByLocation(
  results: readonly DocsSearchResult[]
): DocsSearchResult[] {
  const byLocation = new Map<string, DocsSearchResult>();

  for (const result of results) {
    const key = blockResultLocationKey(result);
    const existing = byLocation.get(key);
    if (!existing) {
      byLocation.set(key, result);
      continue;
    }

    if (result.score > existing.score) {
      byLocation.set(key, {
        ...existing,
        ...result,
        sourceType: existing.sourceType,
        databaseId: existing.databaseId ?? result.databaseId,
        databaseRowId: existing.databaseRowId ?? result.databaseRowId,
        databaseRowShardDocId:
          existing.databaseRowShardDocId ?? result.databaseRowShardDocId,
        databaseColumnId: existing.databaseColumnId ?? result.databaseColumnId,
        visibility: existing.visibility ?? result.visibility,
        technicalSourceKind:
          existing.technicalSourceKind ?? result.technicalSourceKind,
      });
      continue;
    }

    byLocation.set(key, {
      ...existing,
      ...(!existing.blockContent && result.blockContent
        ? { blockContent: result.blockContent }
        : {}),
      // Ranking decides presentation, never navigation identity. A local hit
      // can win by score/content while only the remote cell index knows the
      // exact row-shard document required for cross-shard fullscreen focus.
      databaseId: existing.databaseId ?? result.databaseId,
      databaseRowId: existing.databaseRowId ?? result.databaseRowId,
      databaseRowShardDocId:
        existing.databaseRowShardDocId ?? result.databaseRowShardDocId,
      databaseColumnId: existing.databaseColumnId ?? result.databaseColumnId,
      visibility: existing.visibility ?? result.visibility,
      technicalSourceKind:
        existing.technicalSourceKind ?? result.technicalSourceKind,
    });
  }

  return [...byLocation.values()];
}

function blockResultLocationKey(result: DocsSearchResult) {
  return getDocsSearchLocationIdentity(result);
}

export type BlockRow =
  | {
      kind: 'block-doc-header';
      docId: string;
      count: number;
      /** Best hit score; drives score-sort when the group is collapsed. */
      score: number;
      bestSnippet?: string;
      bestTitle?: string;
      bestSourceType: SearchSourceType;
      sourceTypes: SearchSourceType[];
    }
  | {
      kind: 'block-source';
      docId: string;
      title?: string;
      blockId?: string;
      elementId?: string;
      parentBlockId?: string;
      parentFlavour?: string;
      noteBlockId?: string;
      displayMode?: string;
      sourceType: SearchSourceType;
      snippet?: string;
      databaseId?: string;
      databaseRowId?: string;
      databaseRowShardDocId?: string;
      databaseColumnId?: string;
      databaseTitle?: string;
      databaseRowLabel?: string;
      databaseColumnName?: string;
      visibility?: 'semantic' | 'technical';
      technicalSourceKind?: 'mail_storage';
      score: number;
      /** 0 = standalone single-hit doc, 1 = nested under an expanded header. */
      indent: 0 | 1;
    }
  | {
      kind: 'block-load-more';
      /** load-more list key, see {@link blockSourcesListKey}. */
      listKey: string;
      docId: string;
      hidden: number;
      sourceTypes: SearchSourceType[];
    };

export interface BlockRowState {
  /** Doc IDs whose multi-hit header is expanded. */
  expandedDocs: ReadonlySet<string>;
  /** Load-more list keys that are fully expanded. */
  expandedLists: ReadonlySet<string>;
}

/**
 * Turn flat block results into a bounded row list:
 * - a doc with a single hit becomes one navigable standalone row;
 * - a doc with several hits becomes an expand-only header (with a hit count);
 *   when expanded it yields up to `limit` nested source rows plus a load-more
 *   row reporting the remainder.
 */
export function buildBlockRows(
  results: readonly DocsSearchResult[],
  state: BlockRowState,
  limit = BLOCK_SOURCES_PER_DOC_LIMIT
): BlockRow[] {
  const rows: BlockRow[] = [];

  for (const group of groupBlockResultsByDoc(
    dedupeBlockResultsByLocation(results)
  )) {
    if (group.sources.length === 1) {
      const source = group.best;
      rows.push({
        kind: 'block-source',
        docId: source.docId,
        title: source.title,
        blockId: source.blockId,
        ...(source.elementId ? { elementId: source.elementId } : {}),
        parentBlockId: source.parentBlockId,
        parentFlavour: source.parentFlavour,
        noteBlockId: source.noteBlockId,
        displayMode: source.displayMode,
        sourceType: source.sourceType,
        snippet: source.blockContent,
        databaseId: source.databaseId,
        databaseRowId: source.databaseRowId,
        databaseRowShardDocId: source.databaseRowShardDocId,
        databaseColumnId: source.databaseColumnId,
        databaseTitle: source.databaseTitle,
        databaseRowLabel: source.databaseRowLabel,
        databaseColumnName: source.databaseColumnName,
        visibility: source.visibility,
        technicalSourceKind: source.technicalSourceKind,
        score: source.score,
        indent: 0,
      });
      continue;
    }

    rows.push({
      kind: 'block-doc-header',
      docId: group.docId,
      count: group.sources.length,
      score: group.best.score,
      bestSnippet: group.best.blockContent,
      bestTitle: group.best.title,
      bestSourceType: group.best.sourceType,
      sourceTypes: uniqueSourceTypes(
        group.sources.map(source => source.sourceType)
      ),
    });

    if (!state.expandedDocs.has(group.docId)) continue;

    const listKey = blockSourcesListKey(group.docId);
    const { visible, hidden } = splitForLoadMore(
      group.sources,
      limit,
      state.expandedLists.has(listKey)
    );
    const hiddenSources = group.sources.slice(visible.length);
    for (const source of visible) {
      rows.push({
        kind: 'block-source',
        docId: source.docId,
        title: source.title,
        blockId: source.blockId,
        ...(source.elementId ? { elementId: source.elementId } : {}),
        parentBlockId: source.parentBlockId,
        parentFlavour: source.parentFlavour,
        noteBlockId: source.noteBlockId,
        displayMode: source.displayMode,
        sourceType: source.sourceType,
        snippet: source.blockContent,
        databaseId: source.databaseId,
        databaseRowId: source.databaseRowId,
        databaseRowShardDocId: source.databaseRowShardDocId,
        databaseColumnId: source.databaseColumnId,
        databaseTitle: source.databaseTitle,
        databaseRowLabel: source.databaseRowLabel,
        databaseColumnName: source.databaseColumnName,
        visibility: source.visibility,
        technicalSourceKind: source.technicalSourceKind,
        score: source.score,
        indent: 1,
      });
    }
    if (hidden > 0) {
      rows.push({
        kind: 'block-load-more',
        listKey,
        docId: group.docId,
        hidden,
        sourceTypes: uniqueSourceTypes(
          hiddenSources.map(source => source.sourceType)
        ),
      });
    }
  }

  return rows;
}

function uniqueSourceTypes(types: readonly SearchSourceType[]) {
  return [...new Set(types)];
}
